// @systeme: interface
// LE point d'accès à l'API. Aucun autre fichier du client ne fait d'appel HTTP.
// Chaque fonction vise une route serveur existante (garde-fou R6) et valide la réponse
// avec le schéma partagé : ce que l'écran reçoit a toujours la forme attendue.
import axios, { AxiosError } from 'axios';
import {
  SchemaBoiteDeReception,
  SchemaCapture,
  SchemaElement,
  SchemaErreurApi,
  SchemaFicheProposee,
  SchemaEtatNotifications,
  SchemaFluxIcs,
  SchemaJourCourant,
  SchemaVueAVenir,
  SchemaVueNotes,
  SchemaVueRepartition,
  SchemaInfoInvitation,
  SchemaDocument,
  SchemaInvitation,
  SchemaMembre,
  SchemaModeleRecurrence,
  SchemaRecurrence,
  SchemaReponseMoi,
  SchemaReponseSession,
  SchemaRetour,
  SchemaVueAujourdhui,
  SchemaApercuRetroplanning,
  SchemaModeleRetroplanning,
  SchemaRetroplanning,
  SchemaArticle,
  SchemaListe,
  SchemaListePartagee,
  SchemaResumeListe,
  SchemaSuggestion,
  SchemaEtatAbonnement,
  SchemaLienExterne,
  SchemaBancEssai,
  SchemaPlanAdmin,
  SchemaPromptAdmin,
  SchemaReglageAdmin,
  SchemaTableauDeBord,
  SchemaInterrupteurs,
  SchemaFoyerAdmin,
  SchemaRetourAdmin,
  SchemaStatsAppels,
  SchemaEntreeAudit,
  SchemaAccesSupport,
  SchemaVueSupport,
  SchemaEtatDeuxFacteurs,
  type FoyerAdmin,
  type RetourAdmin,
  type StatsAppels,
  type EntreeAudit,
  type AccesSupport,
  type OuvertureSupport,
  type VueSupport,
  type EtatDeuxFacteurs,
  SchemaTexteJuridique,
  SchemaBilanSemaine,
  SchemaFoyerResume,
  type FoyerResume,
  type BilanSemaine,
  type CleJuridique,
  type TexteJuridique,
  type BancEssai,
  type CreationVersionPrompt,
  type Interrupteurs,
  type LancementBanc,
  type MajPlan,
  type PlanAdmin,
  type PromptAdmin,
  type ReglageAdmin,
  type TableauDeBord,
  type DemandePaiement,
  type EtatAbonnement,
  type AbonnementPush,
  type BoiteDeReception,
  type Capture,
  type Connexion,
  type CorrectionTexte,
  type CreationCaptureTexte,
  type FicheProposee,
  type MajFiche,
  type ReponseQuestion,
  type ValidationFiche,
  type CreationElement,
  type CreationRecurrence,
  type CreationRetour,
  type Element,
  type ErreurApi,
  type EtatNotifications,
  type FluxIcs,
  type JourCourant,
  type PorteeAVenir,
  type VueAVenir,
  type VueNotes,
  type VueRepartition,
  type CreationInvitation,
  type InfoInvitation,
  type Document,
  type Invitation,
  type Membre,
  type Inscription,
  type MajElement,
  type MajProfil,
  type MajRecurrence,
  type ModeleRecurrence,
  type Recurrence,
  type MotDePasseOublie,
  type PreferencesNotifications,
  type Reinitialisation,
  type ReponseMoi,
  type ReponseSession,
  type Report,
  type Retour,
  type VueAujourdhui,
  type AjoutJalon,
  type ApercuRetroplanning,
  type AvisJalon,
  type CreationRetroplanning,
  type DemandeApercu,
  type ModeleRetroplanning,
  type Retroplanning,
  type Article,
  type CreationArticle,
  type Liste,
  type ListePartagee,
  type MajArticle,
  type ResumeListe,
  type Suggestion,
} from '@foyer/shared';
import { z } from 'zod';

const http = axios.create({ baseURL: '/', withCredentials: true, timeout: 15_000 });

/** L'erreur telle que les écrans la reçoivent : toujours un message pour l'utilisatrice. */
export class ErreurClient extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly statut: number | undefined,
    public readonly details?: Record<string, string[]>,
  ) {
    super(message);
  }
}

const MESSAGE_RESEAU = 'Pas de réseau pour le moment. Je réessaie dès qu’il revient.';
const MESSAGE_TECHNIQUE = 'Ça n’a pas marché de mon côté. Je réessaie dans un instant.';

function versErreurClient(erreur: unknown): ErreurClient {
  if (erreur instanceof AxiosError) {
    if (!erreur.response) return new ErreurClient('reseau', MESSAGE_RESEAU, undefined);
    const corps = SchemaErreurApi.safeParse(erreur.response.data);
    if (corps.success) {
      const e: ErreurApi = corps.data;
      return new ErreurClient(e.code, e.message, erreur.response.status, e.details);
    }
    return new ErreurClient('erreur_technique', MESSAGE_TECHNIQUE, erreur.response.status);
  }
  return new ErreurClient('erreur_technique', MESSAGE_TECHNIQUE, undefined);
}

/** Les écrans peuvent s'abonner à la perte de session pour renvoyer vers la connexion. */
let surSessionPerdue: (() => void) | undefined;
export function quandSessionPerdue(rappel: () => void): void {
  surSessionPerdue = rappel;
}

async function lire<S extends z.ZodType>(
  schema: S,
  requete: Promise<{ data: unknown }>,
): Promise<z.infer<S>> {
  try {
    const { data } = await requete;
    return schema.parse(data);
  } catch (erreur) {
    const e = versErreurClient(erreur);
    if (e.statut === 401 && e.code === 'non_connectee') surSessionPerdue?.();
    throw e;
  }
}

export const api = {
  auth: {
    inscription: (donnees: Inscription): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.post('/api/auth/inscription', donnees)),
    connexion: (donnees: Connexion): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.post('/api/auth/connexion', donnees)),
    deconnexion: async (): Promise<void> => {
      await http.post('/api/auth/deconnexion');
    },
    /** Sur tous les appareils : un téléphone perdu ne reste pas connecté. */
    deconnexionPartout: async (): Promise<void> => {
      await http.post('/api/auth/deconnexion-partout');
    },
    session: (): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.get('/api/auth/session')),
    motDePasseOublie: async (donnees: MotDePasseOublie): Promise<void> => {
      await http.post('/api/auth/mot-de-passe-oublie', donnees);
    },
    reinitialisation: async (donnees: Reinitialisation): Promise<void> => {
      await http.post('/api/auth/reinitialisation', donnees);
    },
    /** Le lien du courriel de bienvenue. */
    confirmerCourriel: async (jeton: string): Promise<void> => {
      await http.post('/api/auth/verification/confirmer', { jeton });
    },
    renvoyerVerification: async (): Promise<void> => {
      await http.post('/api/auth/verification');
    },
  },
  elements: {
    aujourdhui: (): Promise<VueAujourdhui> =>
      lire(SchemaVueAujourdhui, http.get('/api/elements/aujourdhui')),
    aVenir: (portee: PorteeAVenir, depuis?: string): Promise<VueAVenir> =>
      lire(SchemaVueAVenir, http.get('/api/elements/a-venir', { params: { portee, depuis } })),
    notes: (): Promise<VueNotes> => lire(SchemaVueNotes, http.get('/api/elements/notes')),
    repartition: (): Promise<VueRepartition> =>
      lire(SchemaVueRepartition, http.get('/api/elements/repartition')),
    creer: (donnees: CreationElement): Promise<Element> =>
      lire(SchemaElement, http.post('/api/elements', donnees)),
    modifier: (id: string, donnees: MajElement): Promise<Element> =>
      lire(SchemaElement, http.patch(`/api/elements/${id}`, donnees)),
    faire: (id: string): Promise<Element> =>
      lire(SchemaElement, http.post(`/api/elements/${id}/faire`)),
    reporter: (id: string, report: Report): Promise<Element> =>
      lire(SchemaElement, http.post(`/api/elements/${id}/reporter`, report)),
    supprimer: async (id: string): Promise<void> => {
      await http.delete(`/api/elements/${id}`);
    },
  },
  moi: {
    lire: (): Promise<ReponseMoi> => lire(SchemaReponseMoi, http.get('/api/moi')),
    modifier: (donnees: MajProfil): Promise<ReponseMoi> =>
      lire(SchemaReponseMoi, http.patch('/api/moi', donnees)),
    membres: (): Promise<Membre[]> => lire(z.array(SchemaMembre), http.get('/api/moi/membres')),
    /** Demander la suppression du compte : trente jours pour changer d'avis. */
    demanderSuppression: (): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.post('/api/moi/suppression')),
    annulerSuppression: (): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.delete('/api/moi/suppression')),
    /** L'archive de mes documents : un lien de téléchargement, pas un appel. */
    urlExport: (): string => '/api/moi/export',
    foyers: (): Promise<FoyerResume[]> =>
      lire(z.array(SchemaFoyerResume), http.get('/api/moi/foyers')),
    /** Passer sur un autre de mes foyers : la session change. */
    choisirFoyer: (id: string): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.post(`/api/moi/foyers/${id}/choisir`)),
  },
  invitations: {
    lister: (): Promise<Invitation[]> =>
      lire(z.array(SchemaInvitation), http.get('/api/invitations')),
    creer: (donnees: CreationInvitation): Promise<Invitation> =>
      lire(SchemaInvitation, http.post('/api/invitations', donnees)),
    annuler: async (id: string): Promise<void> => {
      await http.delete(`/api/invitations/${id}`);
    },
    /** Ce que dit un lien d'invitation, avant tout compte. */
    info: (jeton: string): Promise<InfoInvitation> =>
      lire(SchemaInfoInvitation, http.get(`/api/rejoindre/${jeton}`)),
    /** Avec un compte : rejoindre le foyer ; la session bascule dessus. */
    accepter: (jeton: string): Promise<ReponseSession> =>
      lire(SchemaReponseSession, http.post(`/api/invitations/${jeton}/accepter`)),
  },
  recurrences: {
    catalogue: (): Promise<ModeleRecurrence[]> =>
      lire(z.array(SchemaModeleRecurrence), http.get('/api/recurrences/catalogue')),
    lister: (): Promise<Recurrence[]> =>
      lire(z.array(SchemaRecurrence), http.get('/api/recurrences')),
    creer: (donnees: CreationRecurrence): Promise<Recurrence> =>
      lire(SchemaRecurrence, http.post('/api/recurrences', donnees)),
    modifier: (id: string, donnees: MajRecurrence): Promise<Recurrence> =>
      lire(SchemaRecurrence, http.patch(`/api/recurrences/${id}`, donnees)),
    supprimer: async (id: string): Promise<void> => {
      await http.delete(`/api/recurrences/${id}`);
    },
  },
  captures: {
    boite: (): Promise<BoiteDeReception> => lire(SchemaBoiteDeReception, http.get('/api/captures')),
    texte: (donnees: CreationCaptureTexte): Promise<Capture> =>
      lire(SchemaCapture, http.post('/api/captures/texte', donnees)),
    /** Le vocal part tel quel (corps brut), le mode et la clé client en en-têtes. */
    audio: (fichier: Blob, mode: 'normal' | 'vide_ma_tete', cleClient: string): Promise<Capture> =>
      lire(
        SchemaCapture,
        http.post('/api/captures/audio', fichier, {
          headers: {
            'Content-Type': fichier.type || 'audio/webm',
            'X-Mode': mode,
            'X-Cle-Client': cleClient,
          },
          timeout: 120_000,
        }),
      ),
    /** La photo d'un document part telle quelle ; « santé » est dit avant l'envoi (DEC-019). */
    photo: (fichier: Blob, sante: boolean, cleClient: string, nom: string): Promise<Capture> =>
      lire(
        SchemaCapture,
        http.post('/api/captures/photo', fichier, {
          headers: {
            'Content-Type': fichier.type || 'image/jpeg',
            'X-Sante': sante ? '1' : '0',
            'X-Cle-Client': cleClient,
            'X-Nom': encodeURIComponent(nom),
          },
          timeout: 120_000,
        }),
      ),
    lire: (id: string): Promise<Capture> => lire(SchemaCapture, http.get(`/api/captures/${id}`)),
    retraiter: (id: string): Promise<Capture> =>
      lire(SchemaCapture, http.post(`/api/captures/${id}/retraiter`)),
    supprimer: async (id: string): Promise<void> => {
      await http.delete(`/api/captures/${id}`);
    },
  },
  fiches: {
    repondre: (id: string, reponse: ReponseQuestion): Promise<FicheProposee> =>
      lire(SchemaFicheProposee, http.post(`/api/fiches/${id}/repondre`, reponse)),
    modifier: (id: string, maj: MajFiche): Promise<FicheProposee> =>
      lire(SchemaFicheProposee, http.patch(`/api/fiches/${id}`, maj)),
    corriger: (id: string, correction: CorrectionTexte): Promise<FicheProposee> =>
      lire(SchemaFicheProposee, http.post(`/api/fiches/${id}/corriger`, correction)),
    valider: (id: string, options: ValidationFiche): Promise<FicheProposee> =>
      lire(SchemaFicheProposee, http.post(`/api/fiches/${id}/valider`, options)),
    rejeter: async (id: string): Promise<void> => {
      await http.post(`/api/fiches/${id}/rejeter`);
    },
  },
  retroplannings: {
    modeles: (): Promise<ModeleRetroplanning[]> =>
      lire(z.array(SchemaModeleRetroplanning), http.get('/api/retroplannings/modeles')),
    apercu: (demande: DemandeApercu): Promise<ApercuRetroplanning> =>
      lire(SchemaApercuRetroplanning, http.post('/api/retroplannings/apercu', demande)),
    lister: (): Promise<Retroplanning[]> =>
      lire(z.array(SchemaRetroplanning), http.get('/api/retroplannings')),
    creer: (donnees: CreationRetroplanning): Promise<Retroplanning> =>
      lire(SchemaRetroplanning, http.post('/api/retroplannings', donnees)),
    lire: (id: string): Promise<Retroplanning> =>
      lire(SchemaRetroplanning, http.get(`/api/retroplannings/${id}`)),
    /** La préparation d'un élément, ou rien s'il n'en a pas. */
    deElement: async (elementId: string): Promise<Retroplanning | undefined> => {
      try {
        return await lire(
          SchemaRetroplanning,
          http.get(`/api/retroplannings/element/${elementId}`),
        );
      } catch (e) {
        if (e instanceof ErreurClient && e.statut === 404) return undefined;
        throw e;
      }
    },
    propager: (id: string): Promise<Retroplanning> =>
      lire(SchemaRetroplanning, http.post(`/api/retroplannings/${id}/propager`)),
    ajouterJalon: (id: string, ajout: AjoutJalon): Promise<Retroplanning> =>
      lire(SchemaRetroplanning, http.post(`/api/retroplannings/${id}/jalons`, ajout)),
    donnerAvis: (jalonId: string, avis: AvisJalon): Promise<Retroplanning> =>
      lire(SchemaRetroplanning, http.post(`/api/retroplannings/jalons/${jalonId}/avis`, { avis })),
    supprimer: async (id: string): Promise<void> => {
      await http.delete(`/api/retroplannings/${id}`);
    },
  },
  listes: {
    lister: (): Promise<ResumeListe[]> => lire(z.array(SchemaResumeListe), http.get('/api/listes')),
    creer: (nom: string): Promise<Liste> => lire(SchemaListe, http.post('/api/listes', { nom })),
    lire: (id: string): Promise<Liste> => lire(SchemaListe, http.get(`/api/listes/${id}`)),
    renommer: (id: string, nom: string): Promise<Liste> =>
      lire(SchemaListe, http.patch(`/api/listes/${id}`, { nom })),
    supprimer: async (id: string): Promise<void> => {
      await http.delete(`/api/listes/${id}`);
    },
    suggestions: (id: string): Promise<Suggestion[]> =>
      lire(z.array(SchemaSuggestion), http.get(`/api/listes/${id}/suggestions`)),
    ajouter: (id: string, article: CreationArticle): Promise<Article> =>
      lire(SchemaArticle, http.post(`/api/listes/${id}/articles`, article)),
    modifierArticle: (id: string, articleId: string, maj: MajArticle): Promise<Article> =>
      lire(SchemaArticle, http.patch(`/api/listes/${id}/articles/${articleId}`, maj)),
    retirer: async (id: string, articleId: string): Promise<void> => {
      await http.delete(`/api/listes/${id}/articles/${articleId}`);
    },
    vider: (id: string): Promise<Liste> => lire(SchemaListe, http.post(`/api/listes/${id}/vider`)),
    partager: (id: string): Promise<Liste> =>
      lire(SchemaListe, http.post(`/api/listes/${id}/partage`)),
    arreterPartage: (id: string): Promise<Liste> =>
      lire(SchemaListe, http.delete(`/api/listes/${id}/partage`)),
  },
  documents: {
    deElement: (elementId: string): Promise<Document[]> =>
      lire(z.array(SchemaDocument), http.get('/api/documents', { params: { element: elementId } })),
    /** L'adresse du fichier, pour une balise image : le cookie fait l'authentification. */
    urlFichier: (id: string): string => `/api/documents/${id}/fichier`,
    supprimer: async (id: string): Promise<void> => {
      await http.delete(`/api/documents/${id}`);
    },
  },
  /** Une liste ouverte par son lien, sans compte. */
  partage: {
    lire: (jeton: string): Promise<ListePartagee> =>
      lire(SchemaListePartagee, http.get(`/api/partage/${jeton}`)),
    cocher: (jeton: string, articleId: string, coche: boolean): Promise<ListePartagee> =>
      lire(
        SchemaListePartagee,
        http.patch(`/api/partage/${jeton}/articles/${articleId}`, { coche }),
      ),
  },
  coaching: {
    /** « Ma semaine » : calculé quand elle l'ouvre, jamais poussé. */
    maSemaine: (): Promise<BilanSemaine> =>
      lire(SchemaBilanSemaine, http.get('/api/coaching/ma-semaine')),
  },
  juridique: {
    /** Un texte légal : public, lisible avant l'inscription. */
    lire: (cle: CleJuridique): Promise<TexteJuridique> =>
      lire(SchemaTexteJuridique, http.get(`/api/juridique/${cle}`)),
  },
  temps: {
    aujourdhui: (): Promise<JourCourant> =>
      lire(SchemaJourCourant, http.get('/api/temps/aujourdhui')),
  },
  ics: {
    lire: (): Promise<FluxIcs> => lire(SchemaFluxIcs, http.get('/api/ics')),
    regenerer: (): Promise<FluxIcs> => lire(SchemaFluxIcs, http.post('/api/ics/regenerer')),
  },
  notifications: {
    etat: (): Promise<EtatNotifications> =>
      lire(SchemaEtatNotifications, http.get('/api/notifications')),
    abonner: (abonnement: AbonnementPush): Promise<EtatNotifications> =>
      lire(SchemaEtatNotifications, http.post('/api/notifications/abonnement', abonnement)),
    desabonner: (endpoint: string): Promise<EtatNotifications> =>
      lire(
        SchemaEtatNotifications,
        http.delete('/api/notifications/abonnement', { data: { endpoint } }),
      ),
    preferences: (preferences: Partial<PreferencesNotifications>): Promise<EtatNotifications> =>
      lire(SchemaEtatNotifications, http.patch('/api/notifications/preferences', preferences)),
    essai: async (): Promise<void> => {
      await http.post('/api/notifications/essai');
    },
  },
  abonnement: {
    etat: (): Promise<EtatAbonnement> => lire(SchemaEtatAbonnement, http.get('/api/abonnement')),
    /** La page de paiement Stripe, à ouvrir dans l'onglet courant. */
    payer: (donnees: DemandePaiement): Promise<{ url: string }> =>
      lire(SchemaLienExterne, http.post('/api/abonnement/paiement', donnees)),
    /** Le portail Stripe : factures, carte, changement de formule. */
    portail: (): Promise<{ url: string }> =>
      lire(SchemaLienExterne, http.post('/api/abonnement/portail')),
    resilier: (): Promise<EtatAbonnement> =>
      lire(SchemaEtatAbonnement, http.post('/api/abonnement/resilier')),
    reprendre: (): Promise<EtatAbonnement> =>
      lire(SchemaEtatAbonnement, http.delete('/api/abonnement/resilier')),
  },
  /** L'espace de Juliette : réservé aux adresses de ADMIN_COURRIELS, le serveur vérifie. */
  admin: {
    tableauDeBord: (): Promise<TableauDeBord> =>
      lire(SchemaTableauDeBord, http.get('/api/admin/tableau-de-bord')),
    reglages: (): Promise<ReglageAdmin[]> =>
      lire(z.array(SchemaReglageAdmin), http.get('/api/admin/reglages')),
    modifierReglage: (cle: string, valeur: unknown): Promise<ReglageAdmin> =>
      lire(SchemaReglageAdmin, http.put(`/api/admin/reglages/${cle}`, { valeur })),
    interrupteurs: (): Promise<Interrupteurs> =>
      lire(SchemaInterrupteurs, http.get('/api/admin/interrupteurs')),
    plans: (): Promise<PlanAdmin[]> => lire(z.array(SchemaPlanAdmin), http.get('/api/admin/plans')),
    modifierPlan: (cle: string, donnees: MajPlan): Promise<PlanAdmin> =>
      lire(SchemaPlanAdmin, http.patch(`/api/admin/plans/${cle}`, donnees)),
    prompts: (): Promise<PromptAdmin[]> =>
      lire(z.array(SchemaPromptAdmin), http.get('/api/admin/prompts')),
    creerVersionPrompt: (donnees: CreationVersionPrompt): Promise<PromptAdmin> =>
      lire(SchemaPromptAdmin, http.post('/api/admin/prompts', donnees)),
    activerPrompt: (id: string): Promise<PromptAdmin> =>
      lire(SchemaPromptAdmin, http.post(`/api/admin/prompts/${id}/activer`)),
    bancs: (): Promise<BancEssai[]> => lire(z.array(SchemaBancEssai), http.get('/api/admin/bancs')),
    lancerBanc: (donnees: LancementBanc): Promise<BancEssai> =>
      lire(SchemaBancEssai, http.post('/api/admin/bancs', donnees)),
    foyers: (): Promise<FoyerAdmin[]> =>
      lire(z.array(SchemaFoyerAdmin), http.get('/api/admin/foyers')),
    prolongerEssai: (id: string, essaiFinLe: string): Promise<FoyerAdmin> =>
      lire(SchemaFoyerAdmin, http.patch(`/api/admin/foyers/${id}/essai`, { essaiFinLe })),
    retours: (tous: boolean): Promise<RetourAdmin[]> =>
      lire(
        z.array(SchemaRetourAdmin),
        http.get('/api/admin/retours', { params: tous ? { tous: '1' } : {} }),
      ),
    traiterRetour: (id: string, reponse?: string): Promise<RetourAdmin> =>
      lire(SchemaRetourAdmin, http.post(`/api/admin/retours/${id}/traiter`, { reponse })),
    appels: (): Promise<StatsAppels> => lire(SchemaStatsAppels, http.get('/api/admin/appels')),
    journal: (): Promise<EntreeAudit[]> =>
      lire(z.array(SchemaEntreeAudit), http.get('/api/admin/journal')),
    accesSupport: (): Promise<AccesSupport[]> =>
      lire(z.array(SchemaAccesSupport), http.get('/api/admin/support')),
    ouvrirSupport: (donnees: OuvertureSupport): Promise<AccesSupport> =>
      lire(SchemaAccesSupport, http.post('/api/admin/support', donnees)),
    vueSupport: (id: string): Promise<VueSupport> =>
      lire(SchemaVueSupport, http.get(`/api/admin/support/${id}`)),
    revoquerSupport: (id: string): Promise<AccesSupport> =>
      lire(SchemaAccesSupport, http.delete(`/api/admin/support/${id}`)),
    /** La double authentification : l'état (et le secret tant qu'elle n'est pas confirmée). */
    deuxFacteurs: (): Promise<EtatDeuxFacteurs> =>
      lire(SchemaEtatDeuxFacteurs, http.get('/api/admin/deux-facteurs')),
    presenterCode: (code: string): Promise<EtatDeuxFacteurs> =>
      lire(SchemaEtatDeuxFacteurs, http.post('/api/admin/deux-facteurs', { code })),
  },
  retours: {
    envoyer: (donnees: CreationRetour): Promise<Retour> =>
      lire(SchemaRetour, http.post('/api/retours', donnees)),
    lister: (): Promise<Retour[]> => lire(z.array(SchemaRetour), http.get('/api/retours')),
  },
};
